import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Platform, View, Text, StyleSheet, Dimensions, TouchableOpacity } from 'react-native';
import Modal from 'react-native-modal';
import { Picker } from '@react-native-picker/picker';
import IonIcons from '@expo/vector-icons/Ionicons';

import Colors from '../../constants/Colors';
import timeUtils from '../../utils/timeUtils';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  contentContainer: {
    flex: 1,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginVertical: 'auto',
    backgroundColor: Colors.white,
    maxWidth: 400,
    minWidth: 300,
    maxHeight: Platform.OS === 'ios' ? 320 : 240,
  },
  header: {
    height: 66,
    backgroundColor: Colors.primaryColor,
    justifyContent: 'center',
    paddingLeft: 24,
  },
  headerText: {
    color: Colors.white,
    fontSize: 20,
  },
  body: {
    flex: 1,
    flexDirection: 'column',
    paddingTop: 10,
    paddingBottom: Platform.OS === 'web' ? 10 : 0,
    paddingHorizontal: 16,
  },
  dropdownRow: {
    flexDirection: Platform.OS === 'android' ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dateDropdown: {
    height: 50,
    borderColor: Colors.white,
  },
  timeDropdown: {
    height: 50,
    borderColor: Colors.white,
  },
  dropdownItem: {
    fontSize: 15,
  },
  icon: {
    color: Colors.lightGray,
  },
  buttonRow: {
    height: 52,
    backgroundColor: Colors.lightGrayBackground,
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 8,
    paddingRight: 8,
  },
  disabledButton: {
    padding: 10,
    backgroundColor: Colors.grayButton,
  },
  confirmButton: {
    padding: 10,
    marginBottom: 6,
    backgroundColor: Colors.primaryColor,
    borderRadius: 4,
  },
  cancelText: {
    marginTop: 10,
    marginRight: 20,
    textTransform: 'uppercase',
    fontWeight: '500',
    color: Colors.black,
  },
  buttonText: {
    color: Colors.white,
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  warningContainer: {
    display: 'flex',
    paddingLeft: 16,
    paddingRight: 16,
    flexShrink: 1,
  },
  warningText: {
    fontWeight: '500',
    fontSize: 18,
    color: Colors.black,
  },
});

class MakeAppointment extends PureComponent {
  static propTypes = {
    options: PropTypes.array.isRequired,
    pantry: PropTypes.objectOf(PropTypes.any).isRequired,
    visit: PropTypes.objectOf(PropTypes.any),
    makeAppointment: PropTypes.func.isRequired,
    changeAppointment: PropTypes.func.isRequired,
    closeMakeAppointment: PropTypes.func.isRequired,
    translation: PropTypes.objectOf(PropTypes.any).isRequired,
    locale: PropTypes.string.isRequired,
  };

  constructor(props) {
    super();
    this.state = {
      submitEnabled: true,
      serviceDate: null,
      time: null,
      times: [],
      options: [],
    };
  }

  componentDidMount = () => {
    const { options } = this.props;
    const useOptions = [];
    options?.forEach((option) => {
      const times =
        option.blocks?.flatMap((block) =>
          block.slots
            ?.filter((slot) => slot.spaces > 0)
            .map((slot) => ({
              from: slot.from,
              until: slot.until,
              service_type: block.service_type,
            }))
        ) ?? [];
      if (times.length > 0) {
        useOptions.push({
          ...option,
          times,
        });
      }
    });

    const times = useOptions?.[0]?.times ?? [];
    const serviceDate = useOptions?.[0]?.date ?? null;

    this.setState({
      options: useOptions ?? [],
      times,
      serviceDate,
      time: times[0],
    });
  };

  updateDate = (date) => {
    let times = this.getTimesFromDate(date);
    this.setState({ serviceDate: date, times, time: times[0] || null });
  };

  getTimesFromDate = (date) => {
    const { options } = this.state;
    let times = [];
    if (options && options.length) {
      options.forEach((option) => {
        if (option.date === date) times = option.times;
      });
    }

    return times;
  };

  updateTime = (time) => {
    this.setState({ time });
  };

  makeAppointment = () => {
    const { pantry: organisation, visit, makeAppointment, changeAppointment } = this.props;
    const { serviceDate, time } = this.state;
    if (visit) {
      changeAppointment(visit.id, serviceDate, time.from ?? time);
    } else {
      makeAppointment(organisation.pantry_id, serviceDate, time.from ?? time);
    }
    this.setState({ submitEnabled: false });
  };

  render() {
    const { translation, locale, closeMakeAppointment, visit } = this.props;
    const { submitEnabled, serviceDate, options, time, times } = this.state;

    const labels = {
      visitTime: translation.pick_visit_time,
      cancel: translation.app_button_cancel,
      confirm: translation.app_button_confirm,
      today: translation.app_today,
      noOptions: translation.no_visit_options_available,
      ok: translation.app_button_ok,
    };
    const dateSelectItems = [];
    const timeSelectItems = [];

    const timeDropdownStyles = {
      width: Platform.OS === 'android' ? 200 : 145,
    };

    const dateDropdownStyles = {
      width: Platform.OS === 'android' ? 200 : 155,
    };

    if (options?.length) {
      const uniqueDates = [];
      options.forEach((option, index) => {
        if (uniqueDates.indexOf(option.date) === -1) {
          uniqueDates.push(option.date);
          const label = timeUtils.getShortDate(option.date, locale, labels.today);
          dateSelectItems.push(<Picker.Item key={`Date-${index}`} label={label} value={option.date} />);
        }
      });
    }

    if (times?.length) {
      times.forEach((serviceTime, index) => {
        let label = '';
        if (serviceTime.service_type === 3) {
          // pre-registration
          label = `${timeUtils.timeSinceMidnightToString(
            serviceTime.from
          )} - ${timeUtils.timeSinceMidnightToString(serviceTime.until)}`;
        } else {
          label = timeUtils.timeSinceMidnightToString(serviceTime.from);
        }
        const color = serviceTime.from === time ? Colors.primary : Colors.black;
        timeSelectItems.push(
          <Picker.Item key={`Time-${index}`} label={label} color={color} value={serviceTime.from} />
        );
      });
    }

    const deviceWidth = Dimensions.get('window').width;
    const deviceHeight = Dimensions.get('window').height;

    const iosProps = getIosProps();

    return (
      <View style={styles.container}>
        <Modal isVisible deviceWidth={deviceWidth} deviceHeight={deviceHeight}>
          <View style={styles.contentContainer}>
            <View style={styles.header}>
              <Text style={styles.headerText}>{labels.visitTime}</Text>
            </View>
            <View style={styles.body}>
              {options && options.length ? (
                <View style={styles.dropdownRow}>
                  <Picker
                    selectedValue={serviceDate}
                    mode="dropdown"
                    style={[styles.dateDropdown, dateDropdownStyles]}
                    itemStyle={styles.dropdownItem}
                    onValueChange={(itemValue) => this.updateDate(itemValue)}
                    {...iosProps}
                  >
                    {dateSelectItems}
                  </Picker>
                  <Picker
                    selectedValue={time}
                    style={[styles.timeDropdown, timeDropdownStyles]}
                    mode="dropdown"
                    itemStyle={styles.dropdownItem}
                    onValueChange={(itemValue) => this.updateTime(itemValue)}
                    {...iosProps}
                  >
                    {timeSelectItems}
                  </Picker>
                </View>
              ) : (
                <View style={styles.warningContainer}>
                  <Text styles={styles.warningText}>{labels.noOptions}</Text>
                </View>
              )}
            </View>
            {options && options.length ? (
              <View style={styles.buttonRow}>
                <TouchableOpacity onPress={closeMakeAppointment}>
                  <Text style={styles.cancelText}>{labels.cancel}</Text>
                </TouchableOpacity>
                {submitEnabled ? (
                  <TouchableOpacity onPress={this.makeAppointment} style={styles.confirmButton}>
                    <Text style={styles.buttonText}>{labels.confirm}</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.disabledButton}>
                    <Text styles={styles.buttonText}>{labels.confirm}</Text>
                  </View>
                )}
              </View>
            ) : (
              <View style={styles.buttonRow}>
                <TouchableOpacity onPress={closeMakeAppointment} style={styles.confirmButton}>
                  <Text style={styles.buttonText}>{labels.ok}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </Modal>
      </View>
    );
  }
}

export default MakeAppointment;

const getIosProps = () => {
  const iosProps = {
    iosIcon: <IonIcons name="caret-down-outline" style={styles.icon} />,
  };

  if (Platform.OS === 'web') {
    delete iosProps.iosIcon;
  }
  return iosProps;
};
/**
 *
 * [ { date: '2020-06-26', blocks: [ [Object] ] },
  { date: '2020-06-27', blocks: [ [Object] ] } ]
[ { id: 8085,
    createdAt: 2020-05-24T17:01:14.000Z,
    updatedAt: 2020-06-03T12:59:06.000Z,
    deleted: false,
    pantry_id: 70,
    day: 5,
    from_time: 43200,
    until_time: 54000,
    service_type: 2,
    max_people: 25,
    time_interval: 10,
    recurring: true,
    cancelled: false,
    cancelled_block: 0,
    service_date: '2020-06-26',
    walk_in_slots_per_group: 0,
    date: 26,
    service_block_id: 8085,
    waiting_list_slots: 0,
    service_block_instance_id: 60149,
    slots: [{ from: 43200,
    until: 43800,
    regular: { available: 25, used: 19 },
    walkin: { available: 0, used: 0 },
    spaces: 6 },]
 */
